import { useState } from "react";
import { getSubscription } from "../api/enterprise.ts";
import { useAuth } from "../context/auth.tsx";
import { Layout } from "../components/layout.tsx";

type Report = {
  subscription: {
    planTier: string;
    aiTokensUsed: number;
    apiCallsUsed: number;
    storageBytesUsed: number;
    quotaResetsAt: string;
  };
  limits: { aiTokensPerMonth: number; apiCallsPerMonth: number; storageBytesTotal: number };
  utilizationPct: { aiTokens: number; apiCalls: number; storage: number };
};

function Bar({ pct, label }: { pct: number; label: string }) {
  const color = pct >= 90 ? "bg-red-500" : pct >= 70 ? "bg-yellow-500" : "bg-green-500";
  return (
    <div className="space-y-1">
      <div className="flex justify-between text-sm">
        <span className="text-gray-300">{label}</span>
        <span className="text-gray-500">{pct}%</span>
      </div>
      <div className="h-3 w-full rounded bg-gray-800">
        <div className={`h-3 rounded ${color}`} style={{ width: `${Math.min(pct, 100)}%` }} />
      </div>
    </div>
  );
}

export function AdminUsagePage() {
  const { token } = useAuth();
  const [orgId, setOrgId] = useState("");
  const [report, setReport] = useState<Report | null>(null);

  async function load() {
    if (!orgId) return;
    const res = (await getSubscription(token, orgId)) as Report;
    setReport(res);
  }

  const fmt = (n: number) => (n === -1 ? "Unlimited" : n.toLocaleString());

  return (
    <Layout>
      <div className="space-y-6 p-6">
        <h1 className="text-2xl font-bold text-white">Usage Analytics</h1>

        <div className="flex gap-3">
          <input
            className="flex-1 rounded border border-gray-700 bg-gray-800 px-3 py-2 text-white"
            placeholder="Organization ID"
            value={orgId}
            onChange={(e) => setOrgId(e.target.value)}
          />
          <button
            onClick={load}
            className="rounded bg-indigo-600 px-4 py-2 text-white hover:bg-indigo-700"
          >
            Load
          </button>
        </div>

        {report && (
          <>
            <div className="grid grid-cols-3 gap-4">
              {[
                {
                  label: "AI Tokens Used",
                  value: report.subscription.aiTokensUsed.toLocaleString(),
                  sub: `/ ${fmt(report.limits.aiTokensPerMonth)}`,
                },
                {
                  label: "API Calls Used",
                  value: report.subscription.apiCallsUsed.toLocaleString(),
                  sub: `/ ${fmt(report.limits.apiCallsPerMonth)}`,
                },
                {
                  label: "Storage Used",
                  value: `${(report.subscription.storageBytesUsed / 1024 / 1024).toFixed(2)} MB`,
                  sub: `/ ${fmt(report.limits.storageBytesTotal / 1024 / 1024)} MB`,
                },
              ].map(({ label, value, sub }) => (
                <div key={label} className="rounded-lg border border-gray-800 bg-gray-900 p-4">
                  <div className="text-xs text-gray-500">{label}</div>
                  <div className="mt-1 text-2xl font-bold text-white">{value}</div>
                  <div className="text-xs text-gray-600">{sub}</div>
                </div>
              ))}
            </div>

            <div className="space-y-5 rounded-lg border border-gray-800 bg-gray-900 p-5">
              <h2 className="text-lg font-semibold text-white">Quota Utilization</h2>
              <Bar pct={report.utilizationPct.aiTokens} label="AI Tokens" />
              <Bar pct={report.utilizationPct.apiCalls} label="API Calls" />
              <Bar pct={report.utilizationPct.storage} label="Storage" />
            </div>

            <div className="rounded-lg border border-gray-800 bg-gray-900 p-5">
              <div className="text-sm text-gray-400">
                Current plan:{" "}
                <span className="font-medium text-white">{report.subscription.planTier}</span>
                &nbsp;— Quota resets:{" "}
                <span className="text-white">
                  {new Date(report.subscription.quotaResetsAt).toLocaleDateString()}
                </span>
              </div>
            </div>
          </>
        )}
      </div>
    </Layout>
  );
}
